import {
  DateFormatOptions,
  PhoneNumberFormatOptions,
  CurrencyFormatOptions,
  CurrencyDecimalOptions,
} from "blocks/v2/Form/Field/schema";
import {
  FieldBlock,
  FieldBlockConfig,
  FieldBlockDropDownConfig,
} from "blocks/v2/Form/Field/types";
import cuid from "cuid";
import { APIField } from "server/services/tables/types/fields";
import { slugify } from "utils/slugify";

export function formBlockFromTableFields(
  tableFields: APIField[],
  tableId: string
): FieldBlock[] {
  return tableFields.reduce((prev, tf) => {
    const formField = tableFieldToFormField(tf, tableId);
    return formField ? [...prev, formField] : prev;
  }, [] as FieldBlock[]);
}

/**
 * Converts a field from Tables into a Form Field for a form in interfaces.
 * If a table field type that is not supported is encountered, we skip it.
 */
export function tableFieldToFormField(
  tableField: APIField,
  tableId: string | undefined
): FieldBlock | null {
  // Locked fields can be displayed in a table but not in form
  if (tableField.options?.locked) return null;

  const config: Partial<FieldBlockConfig> = {
    id: cuid(),
    name: slugify(tableField.name),
    label: tableField.name,
    nameAutoGenerated: true,
    required: tableField.options?.required,
    tablesFieldKey: `f${tableField.id}`,
  };
  switch (tableField.type) {
    case "string": // present in tablesui
      config.inputType = "text";
      if (tableField.options?.static_choices) {
        Object.assign(config, { ...dropdownFromTableField(tableField) });
      } else if (
        tableId &&
        tableField.options?.choice_provider?.kind === "table"
      ) {
        // Handle table field options
        Object.assign(config, {
          ...dropdownFromDynamicTableField(tableField, tableId),
        });
      }
      break;
    case "labeled_string":
      config.inputType = "text";
      if (tableField.options?.static_choices) {
        Object.assign(config, { ...dropdownFromTableField(tableField) });
      } else if (
        tableId &&
        tableField.options?.choice_provider?.kind === "table"
      ) {
        Object.assign(config, {
          ...dropdownFromDynamicTableField(tableField, tableId),
        });
      }
      break;
    case "multiple_string":
    case "multiple_labeled_string": // present in tablesui as "Dropdown" with multi select
      config.inputType = "text";
      if (tableField.options?.static_choices) {
        // Don't try to convert to a dropdown unless we have the choices available
        Object.assign(config, { ...dropdownFromTableField(tableField) });
      } else if (
        tableId &&
        tableField.options?.choice_provider?.kind === "table"
      ) {
        Object.assign(config, {
          ...dropdownFromDynamicTableField(tableField, tableId),
        });
      }
      break;
    case "text": // present in tablesui
      config.inputType = "textarea";
      break;
    case "boolean":
      config.inputType = "checkbox";
      if (config.inputType === "checkbox") {
        config.text = tableField.name;
      }
      break;
    case "number":
      config.inputType = "number";
      break;
    case "decimal":
      config.inputType = "number";
      break;
    case "email":
      config.inputType = "email";
      break;
    case "link":
      config.inputType = "url";
      break;
    case "datetime": // present in tablesui
      config.inputType = "date-picker";
      if (config.inputType !== "date-picker") throw "type guard";
      config.dateFormat =
        tableField.options?.date_format ?? DateFormatOptions[0].value;
      config.includeTime = tableField.options?.include_time ?? false;
      break;
    case "phone_number":
      config.inputType = "phone-number";
      if (config.inputType !== "phone-number") throw "type guard";
      config.phoneNumberFormat =
        tableField.options?.phone_number_format ??
        PhoneNumberFormatOptions[0].value;
      break;
    case "currency":
      config.inputType = "currency";
      if (config.inputType !== "currency") throw "type guard";
      config.currencyFormat =
        tableField.options?.currency_format ?? CurrencyFormatOptions[0].value;
      config.decimals =
        tableField.options?.decimals ?? CurrencyDecimalOptions[0].value;
      break;
    /**
     * Tables only
     */
    case "formula":
    case "button_trigger_zap":
    case "button_continue_zap":
      return null;
    /**
     * Not yet implemeneted
     */
    case "multiple_text":
    case "multiple_boolean":
    case "multiple_number":
    case "multiple_decimal":
    case "multiple_datetime":
    case "uuid":
    case "multiple_uuid":
    case "json":
    case "multiple_json":
    case "multiple_email":
    default:
      throw new Error(
        `Unsupported field: ${tableField.type}; field: ${tableField.name}`
      );
  }

  if (tableField.options?.static_choices) {
    // This will always convert into a dropdown
    Object.assign(config, { ...dropdownFromTableField(tableField) });
  }

  if (tableId && tableField.options?.choice_provider?.kind === "table") {
    Object.assign(config, {
      ...dropdownFromDynamicTableField(tableField, tableId),
    });
  }

  return {
    type: "field-block",
    config: config as FieldBlockConfig,
  };
}

function dropdownFromTableField(
  tableField: APIField
): Partial<FieldBlockDropDownConfig> {
  // TODO: [Seth]  can we handle choice provider somehow?
  //       [Peter] ^^ yes and we should. But it'll likely take a refactor of
  //               components or endpoints.
  if (!tableField.options?.static_choices)
    throw new Error("Expected static_choices defined on table field");
  return {
    inputType: "dropdown",
    multiSelect:
      tableField.type === "multiple_labeled_string" ||
      tableField.type === "multiple_string",
    options: tableField.options.static_choices.map((c) => c.value).join("\n"),
  };
}

function dropdownFromDynamicTableField(
  tableField: APIField,
  tableId: string
): Partial<FieldBlockDropDownConfig> {
  if (tableField.options?.choice_provider?.kind !== "table")
    throw new Error("Expected a table data source");
  return {
    inputType: "dropdown",
    multiSelect:
      tableField.type === "multiple_labeled_string" ||
      tableField.type === "multiple_string",
    tableId: tableId,
    tableFieldId: tableField.id,
  };
}
